/**
 * Versus Expert — Tier-2 AI support agent console (admin role: master|support).
 *
 * Tab 1 "Shadow Runs" (W1): trigger a read-only agent run against a real user,
 *   then review the persisted session — question, answer, full Anthropic
 *   transcript (text / tool_use / tool_result / thinking blocks), usage, latency.
 *   Acceptance gate: 20+ runs reviewed before the expert goes beyond shadow mode.
 * Tab 2 "Action Queue" (W2): review/approve/decline actions the expert proposes.
 *   Contract is final; the backend follows in a separate PR — a 404 on the
 *   actions endpoint renders a friendly "not deployed yet" state (no crash).
 *
 * Backend: versusapp PR #453 (shadow runs). POST shadow-runs is a live agentic
 * loop and can take minutes — the API client disables its timeout for that call.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  apiService,
  ExpertShadowRunSummary,
  ExpertShadowRun,
  ExpertAction,
  ExpertActionReview,
  ExpertChatTurn,
  ExpertUserContext,
} from '../services/apiService';
import ConfirmModal from '../components/modals/ConfirmModal';
import './VersusExpertPage.css';

// ── helpers ──────────────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const fmtTokens = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
};
const fmtLatency = (ms: number | null | undefined): string => {
  if (ms == null) return '—';
  return ms >= 1000 ? (ms / 1000).toFixed(1) + 's' : ms + 'ms';
};
const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : '—';
const shortId = (id: string | null | undefined): string => (id ? id.slice(0, 8) + '…' : '—');
const pretty = (v: any): string => {
  try {
    return JSON.stringify(v, null, 2) ?? String(v);
  } catch {
    return String(v);
  }
};

const RunStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`xp-badge xp-run-${status}`}>{status}</span>
);
const ActionStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`xp-badge xp-act-${status}`}>{String(status).replace(/_/g, ' ')}</span>
);
const TypeBadge: React.FC<{ type: string }> = ({ type }) => (
  <span className={`xp-type xp-type-${type}`}>{String(type).replace(/_/g, ' ')}</span>
);

// Fable safety rating → human label + colour class (green / amber / red).
const RATING_META: Record<string, { label: string; cls: string }> = {
  safe_to_approve: { label: '✓ Safe to approve', cls: 'xp-rating-safe' },
  review_recommended: { label: '⚠ Review recommended', cls: 'xp-rating-review' },
  high_risk: { label: '⛔ High risk', cls: 'xp-rating-high' },
};
const RatingChip: React.FC<{ rating?: string | null }> = ({ rating }) => {
  if (!rating) return null;
  const meta = RATING_META[rating];
  // Unknown/future rating values still render (raw, neutral) rather than vanish.
  const label = meta?.label ?? String(rating).replace(/_/g, ' ');
  const cls = meta?.cls ?? 'xp-rating-unknown';
  return <span className={`xp-rating ${cls}`}>{label}</span>;
};

// ── page shell (tabs + drawer + new-run modal) ───────────────────────────────

const VersusExpertPage: React.FC = () => {
  const [tab, setTab] = useState<'runs' | 'actions'>('runs');
  const [showNewRun, setShowNewRun] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  // Detail drawer: id to fetch + (optionally) the full run we already hold
  // because POST /shadow-runs returns it — avoids an immediate refetch.
  const [detailRunId, setDetailRunId] = useState<string | null>(null);
  const [preloadedRun, setPreloadedRun] = useState<ExpertShadowRun | null>(null);

  const openRun = useCallback((id: string) => {
    setPreloadedRun(null);
    setDetailRunId(id);
  }, []);

  const handleRunCompleted = (run: ExpertShadowRun) => {
    setShowNewRun(false);
    setRefreshToken((n) => n + 1);
    setTab('runs');
    setPreloadedRun(run);
    setDetailRunId(run?.id ?? null);
  };

  return (
    <div className="versus-expert-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Versus Expert</h1>
          <p className="page-sub">
            Tier-2 AI support agent — shadow runs (W1) &amp; action approval queue (W2)
          </p>
        </div>
        {tab === 'runs' && (
          <button className="button-primary" onClick={() => setShowNewRun(true)}>
            ➕ New Run
          </button>
        )}
      </div>

      <div className="tabs">
        <button
          className={`tab ${tab === 'runs' ? 'active' : ''}`}
          onClick={() => setTab('runs')}
        >
          🕵️ Shadow Runs
        </button>
        <button
          className={`tab ${tab === 'actions' ? 'active' : ''}`}
          onClick={() => setTab('actions')}
        >
          ⚡ Action Queue
        </button>
      </div>

      {tab === 'runs' ? (
        <ShadowRunsTab refreshToken={refreshToken} onOpenRun={openRun} />
      ) : (
        <ActionQueueTab onOpenRun={openRun} />
      )}

      {detailRunId && (
        <RunDetailDrawer
          runId={detailRunId}
          preloaded={preloadedRun && preloadedRun.id === detailRunId ? preloadedRun : null}
          onClose={() => {
            setDetailRunId(null);
            setPreloadedRun(null);
          }}
        />
      )}

      {showNewRun && (
        <NewRunModal onClose={() => setShowNewRun(false)} onCompleted={handleRunCompleted} />
      )}
    </div>
  );
};

// ── tab 1: shadow runs list ──────────────────────────────────────────────────

const PAGE_SIZE = 20;

const ShadowRunsTab: React.FC<{
  refreshToken: number;
  onOpenRun: (id: string) => void;
}> = ({ refreshToken, onOpenRun }) => {
  const [runs, setRuns] = useState<ExpertShadowRunSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notDeployed, setNotDeployed] = useState(false);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiService.getExpertShadowRuns({ limit: PAGE_SIZE });
      setRuns(d?.runs || []);
      setNextCursor(d?.nextCursor ?? null);
      setNotDeployed(false);
    } catch (e: any) {
      if (e?.status === 404) setNotDeployed(true);
      else setError(e?.message || 'Failed to load shadow runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirst();
  }, [loadFirst, refreshToken]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await apiService.getExpertShadowRuns({ limit: PAGE_SIZE, cursor: nextCursor });
      setRuns((prev) => [...prev, ...(d?.runs || [])]);
      setNextCursor(d?.nextCursor ?? null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more runs');
    } finally {
      setLoadingMore(false);
    }
  };

  if (notDeployed) {
    return (
      <div className="xp-panel xp-notdeployed">
        <div className="xp-notdeployed-icon">🚧</div>
        <div className="xp-notdeployed-title">Shadow-run backend not deployed yet</div>
        <div className="xp-notdeployed-sub">
          GET /admin/expert-chat/shadow-runs returned 404 — the Versus Expert backend
          (versusapp PR #453) is not live on this environment.
        </div>
      </div>
    );
  }

  return (
    <div className="xp-panel">
      <div className="xp-toolbar">
        <span className="xp-toolbar-title">Persisted agent sessions — click a row for the full transcript</span>
        <span className="xp-count">
          {loading ? 'loading…' : `${runs.length} run${runs.length === 1 ? '' : 's'} loaded`}
        </span>
        <button className="xp-refresh" onClick={loadFirst} disabled={loading}>
          ⟳ Refresh
        </button>
      </div>

      {error && <div className="xp-error">{error}</div>}

      <div className="xp-table-wrap">
        <table className="xp-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>User</th>
              <th>Question</th>
              <th>Status</th>
              <th>Model</th>
              <th className="num">Latency</th>
              <th className="num">Tokens</th>
            </tr>
          </thead>
          <tbody>
            {!loading && runs.length === 0 && (
              <tr>
                <td colSpan={7} className="xp-empty">
                  No shadow runs yet — trigger the first one via “New Run”.
                </td>
              </tr>
            )}
            {runs.map((r) => (
              <tr
                key={r.id}
                className="xp-row"
                title={r.status === 'error' && r.error ? r.error : `trigger: ${r.trigger || '—'}`}
                onClick={() => onOpenRun(r.id)}
              >
                <td className="xp-dim xp-nowrap">{fmtDate(r.createdAt)}</td>
                <td className="xp-mono" title={r.userId}>{shortId(r.userId)}</td>
                <td className="xp-question" title={r.question}>{r.question}</td>
                <td><RunStatusBadge status={r.status} /></td>
                <td className="xp-dim xp-model" title={r.model}>{r.model || '—'}</td>
                <td className="num">{fmtLatency(r.latencyMs)}</td>
                <td
                  className="num xp-dim"
                  title={
                    r.usage
                      ? `in ${r.usage.input_tokens ?? '—'} · out ${r.usage.output_tokens ?? '—'} · cache read ${r.usage.cache_read_input_tokens ?? '—'} · cache write ${r.usage.cache_creation_input_tokens ?? '—'} · ${r.usage.api_turns ?? '—'} API turns`
                      : 'no usage recorded'
                  }
                >
                  {r.usage
                    ? `${fmtTokens(r.usage.input_tokens)} in · ${fmtTokens(r.usage.output_tokens)} out`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <div className="xp-loadmore">
          <button onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

// ── run detail drawer (question, answer, usage, transcript) ──────────────────

const RunDetailDrawer: React.FC<{
  runId: string;
  preloaded: ExpertShadowRun | null;
  onClose: () => void;
}> = ({ runId, preloaded, onClose }) => {
  const [run, setRun] = useState<ExpertShadowRun | null>(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloaded && preloaded.id === runId) {
      setRun(preloaded);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRun(null);
    apiService
      .getExpertShadowRun(runId)
      .then((d) => {
        if (!cancelled) setRun(d);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(
            e?.status === 404
              ? 'Shadow run not found on this environment.'
              : e?.message || 'Failed to load run',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, preloaded]);

  const hasProposedActions = Array.isArray(run?.proposedActions)
    ? (run?.proposedActions?.length ?? 0) > 0
    : run?.proposedActions != null;

  return (
    <div className="xp-drawer-overlay" onClick={onClose}>
      <div className="xp-drawer" onClick={(e) => e.stopPropagation()}>
        <button className="xp-drawer-close" onClick={onClose}>✕</button>

        {loading && <div className="xp-empty">Loading run…</div>}
        {error && <div className="xp-error">{error}</div>}

        {run && (
          <div className="xp-detail">
            <div className="xp-detail-head">
              <div className="xp-detail-title">
                Shadow Run <span className="xp-mono xp-dim" title={run.id}>{shortId(run.id)}</span>
              </div>
              <div className="xp-detail-meta">
                <RunStatusBadge status={run.status} />
                <span className="xp-chip" title="model">{run.model || '—'}</span>
                <span className="xp-chip" title="latency">⏱ {fmtLatency(run.latencyMs)}</span>
                <span className="xp-chip" title="trigger">trigger: {run.trigger || '—'}</span>
                <span className="xp-chip xp-mono" title={`user ${run.userId}`}>user {shortId(run.userId)}</span>
                {run.sourceConversationId && (
                  <span className="xp-chip xp-mono" title={`source conversation ${run.sourceConversationId}`}>
                    conv {shortId(run.sourceConversationId)}
                  </span>
                )}
                <span className="xp-chip">{fmtDate(run.createdAt)}</span>
              </div>
              {run.usage && (
                <div className="xp-detail-usage">
                  <span>in <b>{fmtTokens(run.usage.input_tokens)}</b></span>
                  <span>out <b>{fmtTokens(run.usage.output_tokens)}</b></span>
                  <span>cache read <b>{fmtTokens(run.usage.cache_read_input_tokens)}</b></span>
                  <span>cache write <b>{fmtTokens(run.usage.cache_creation_input_tokens)}</b></span>
                  <span>API turns <b>{run.usage.api_turns ?? '—'}</b></span>
                </div>
              )}
            </div>

            <div className="xp-section">
              <div className="xp-section-title">Question</div>
              <div className="xp-question-box">{run.question}</div>
            </div>

            <div className="xp-section">
              <div className="xp-section-title">Answer</div>
              {run.answer ? (
                <div className="xp-answer-box">{run.answer}</div>
              ) : (
                <div className="xp-empty">
                  No answer{run.status === 'error' ? ' — run errored' : ''}.
                </div>
              )}
              {run.error && <div className="xp-error xp-error-inline">{run.error}</div>}
            </div>

            {hasProposedActions && (
              <div className="xp-section">
                <div className="xp-section-title">Proposed actions</div>
                <details className="xp-block xp-block-raw" open>
                  <summary>proposedActions JSON</summary>
                  <pre>{pretty(run.proposedActions)}</pre>
                </details>
              </div>
            )}

            <div className="xp-section">
              <div className="xp-section-title">
                Transcript{' '}
                <span className="xp-dim">
                  ({Array.isArray(run.transcript) ? run.transcript.length : 0} messages)
                </span>
              </div>
              <TranscriptViewer transcript={run.transcript} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── transcript viewer (defensive: unknown shapes must never crash) ───────────

const TranscriptViewer: React.FC<{ transcript: any }> = ({ transcript }) => {
  if (!Array.isArray(transcript) || transcript.length === 0) {
    return <div className="xp-empty">No transcript recorded.</div>;
  }
  return (
    <div className="xp-transcript">
      {transcript.map((msg: any, i: number) => (
        <TranscriptMessage key={i} msg={msg} />
      ))}
    </div>
  );
};

const TranscriptMessage: React.FC<{ msg: any }> = ({ msg }) => {
  const roleRaw = msg && typeof msg === 'object' ? msg.role : undefined;
  const role = roleRaw === 'assistant' || roleRaw === 'user' ? roleRaw : 'other';
  const label = typeof roleRaw === 'string' ? roleRaw : 'unknown';
  const content = msg && typeof msg === 'object' ? msg.content : undefined;

  return (
    <div className={`xp-msg xp-msg-${role}`}>
      <div className="xp-msg-role">{label}</div>
      <div className="xp-msg-body">
        {typeof content === 'string' ? (
          <div className="xp-text">{content}</div>
        ) : Array.isArray(content) ? (
          content.map((block: any, i: number) => <ContentBlock key={i} block={block} />)
        ) : (
          <details className="xp-block xp-block-raw">
            <summary>raw message</summary>
            <pre>{pretty(msg)}</pre>
          </details>
        )}
      </div>
    </div>
  );
};

const ContentBlock: React.FC<{ block: any }> = ({ block }) => {
  const type = block && typeof block === 'object' ? block.type : undefined;

  if (type === 'text' && typeof block.text === 'string') {
    return <div className="xp-text">{block.text}</div>;
  }
  if (type === 'tool_use') {
    return (
      <details className="xp-block xp-block-tool">
        <summary>
          → tool: <b>{typeof block.name === 'string' ? block.name : 'unknown'}</b>
        </summary>
        <pre>{pretty(block.input)}</pre>
      </details>
    );
  }
  if (type === 'tool_result') {
    return (
      <details className="xp-block xp-block-result">
        <summary>← tool result{block.is_error ? ' ⚠ (error)' : ''}</summary>
        <pre>{pretty(block.content)}</pre>
      </details>
    );
  }
  // thinking / redacted_thinking / anything unknown → collapsed raw JSON
  return (
    <details className="xp-block xp-block-raw">
      <summary>{typeof type === 'string' ? type : 'unknown block'}</summary>
      <pre>{pretty(block)}</pre>
    </details>
  );
};

// ── new run modal (POST can take minutes — clear progress state) ─────────────

const NewRunModal: React.FC<{
  onClose: () => void;
  onCompleted: (run: ExpertShadowRun) => void;
}> = ({ onClose, onCompleted }) => {
  const [userId, setUserId] = useState('');
  const [question, setQuestion] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const userIdValid = UUID_RE.test(userId.trim());
  const convValid = conversationId.trim() === '' || UUID_RE.test(conversationId.trim());
  const canSubmit = userIdValid && convValid && question.trim().length > 0 && !running;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setRunning(true);
    setElapsed(0);
    setError(null);
    try {
      const run = await apiService.createExpertShadowRun({
        userId: userId.trim(),
        question: question.trim(),
        ...(conversationId.trim() ? { conversationId: conversationId.trim() } : {}),
      });
      onCompleted(run);
    } catch (err: any) {
      if (err?.status === 503) {
        setError(
          'Expert chat is disabled on this environment (EXPERT_CHAT_ENABLED/ANTHROPIC_API_KEY).',
        );
      } else if (err?.status === 404) {
        setError('Unknown userId or conversationId — no matching user/conversation found.');
      } else {
        setError(err?.message || 'Shadow run failed');
      }
      setRunning(false);
    }
  };

  const mins = Math.floor(elapsed / 60);
  const secs = String(elapsed % 60).padStart(2, '0');

  return (
    // While a run is in flight we keep the modal open (the POST only resolves
    // when the agent finishes) — overlay click / cancel are disabled.
    <div className="modal-overlay" onClick={() => !running && onClose()}>
      <div className="modal-content xp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🕵️ New Shadow Run</h2>
        </div>

        {running ? (
          <div className="modal-body">
            <div className="xp-progress">
              <div className="xp-spinner" />
              <div className="xp-progress-title">Expert is investigating…</div>
              <div className="xp-progress-sub">
                The agent is researching this user with read-tools against the live backend.
                This can take several minutes — keep this window open.
              </div>
              <div className="xp-progress-elapsed">
                {mins}:{secs} elapsed
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="xp-error">{error}</div>}

              <div className="xp-form-group">
                <label className="xp-form-label">User ID (uuid) *</label>
                <input
                  className="xp-form-input xp-mono"
                  placeholder="e.g. 6f1d2c3b-…"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  required
                />
                {userId.trim() !== '' && !userIdValid && (
                  <div className="xp-form-hint">Must be a valid UUID.</div>
                )}
              </div>

              <div className="xp-form-group">
                <label className="xp-form-label">Question *</label>
                <textarea
                  className="xp-form-input xp-form-textarea"
                  placeholder="What should the expert investigate for this user?"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={4}
                  required
                />
              </div>

              <div className="xp-form-group">
                <label className="xp-form-label">Conversation ID (uuid, optional)</label>
                <input
                  className="xp-form-input xp-mono"
                  placeholder="link an existing support conversation"
                  value={conversationId}
                  onChange={(e) => setConversationId(e.target.value)}
                />
                {!convValid && <div className="xp-form-hint">Must be a valid UUID (or empty).</div>}
              </div>
            </div>

            <div className="modal-actions xp-modal-actions">
              <button type="button" className="button-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="button-primary" disabled={!canSubmit}>
                Run Expert
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ── tab 2: action approval queue (W2) ────────────────────────────────────────

const ACTION_STATUS_FILTERS = [
  'pending_review',
  'info_requested',
  'proposed',
  'auto_approved',
  'approved',
  'declined',
  'executed',
  'failed',
  'all',
] as const;

/** Human summary of the jsonb payload per action type — falls back to key/value pairs. */
const payloadSummary = (a: ExpertAction): string | null => {
  const p = a?.payload;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  const money = (amount: any, currency: any) =>
    [amount != null ? String(amount) : null, currency != null ? String(currency) : null]
      .filter(Boolean)
      .join(' ');
  try {
    if (a.type === 'grant_voucher') {
      const m = money(p.amount, p.currency);
      const label = p.label ?? p.voucherLabel;
      return `Voucher${m ? ` ${m}` : ''}${label != null ? ` · ${String(label)}` : ''}`;
    }
    if (a.type === 'refund') {
      const m = money(p.amount, p.currency);
      const bet = p.betId || p.bet_id || p.betReference || p.bet_reference;
      return `Refund${m ? ` ${m}` : ''}${bet ? ` · bet ${String(bet)}` : ''}`;
    }
    if (a.type === 'update_kyc_name') {
      const first = p.firstName ?? p.first_name;
      const last = p.lastName ?? p.last_name;
      const full = p.name ?? p.fullName ?? [first, last].filter(Boolean).join(' ');
      return full ? `New name: ${String(full)}` : null;
    }
    if (a.type === 'flag_market_for_review') {
      const market = p.marketId ?? p.market_id;
      return market ? `Market ${String(market)}${p.reason ? ` · ${String(p.reason)}` : ''}` : null;
    }
    if (a.type === 'manual_review') {
      // An escalated case parked for a human: category chip text + the
      // expert's handover reason. Approve = mark handled, nothing executes.
      const category = p.category ? String(p.category).replace(/_/g, ' ') : null;
      const reason = p.reason ? String(p.reason) : null;
      return [category ? `[${category}]` : null, reason].filter(Boolean).join(' ') || null;
    }
    if (a.type === 'self_exclude') {
      const dur = p.duration ?? p.durationDays ?? p.days;
      return dur != null ? `Duration: ${String(dur)}` : null;
    }
    // close_account & unknown types → generic key: value summary (primitives only)
    const parts = Object.entries(p)
      .filter(([, v]) => v == null || ['string', 'number', 'boolean'].includes(typeof v))
      .slice(0, 4)
      .map(([k, v]) => `${k}: ${String(v)}`);
    return parts.length ? parts.join(' · ') : null;
  } catch {
    return null;
  }
};

const ActionQueueTab: React.FC<{ onOpenRun: (sessionId: string) => void }> = ({ onOpenRun }) => {
  const [statusFilter, setStatusFilter] = useState<string>('pending_review');
  const [actions, setActions] = useState<ExpertAction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notDeployed, setNotDeployed] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState<ExpertAction | null>(null);
  const [declining, setDeclining] = useState<ExpertAction | null>(null);
  const [requesting, setRequesting] = useState<ExpertAction | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  // One open user-context drawer at a time (founder 2026-07-04: "man sieht
  // nicht den user … user data, KYC images, balance, badges und alle historien").
  const [contextFor, setContextFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiService.getExpertActions({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
      });
      setActions(d?.actions || []);
      setNextCursor(d?.nextCursor ?? null);
      setNotDeployed(false);
    } catch (e: any) {
      // Feature detection: W2 backend ships in a separate PR — a 404 means the
      // route doesn't exist yet on this environment. Friendly state, no crash.
      if (e?.status === 404) setNotDeployed(true);
      else setError(e?.message || 'Failed to load action queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Live refresh (founder 2026-07-03: a fresh proposal must be visible
  // "instantly", no manual reload — the W2.5 review also attaches async and
  // lands here). Silent first-page refetch every 8s while the tab is visible;
  // paused while the admin is mid-decision (modal open / action in flight) or
  // has paged past page one, so nothing yanks the UI out from under them.
  const pollBlockedRef = useRef(false);
  pollBlockedRef.current = Boolean(
    loading ||
      loadingMore ||
      actingId ||
      confirmApprove ||
      declining ||
      requesting ||
      actions.length > PAGE_SIZE
  );
  useEffect(() => {
    const tick = async () => {
      if (pollBlockedRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const d = await apiService.getExpertActions({
          status: statusFilter === 'all' ? undefined : statusFilter,
          limit: PAGE_SIZE,
        });
        if (pollBlockedRef.current) return; // a modal opened mid-flight — don't clobber
        setActions(d?.actions || []);
        setNextCursor(d?.nextCursor ?? null);
        setNotDeployed(false);
      } catch {
        // silent — visible errors stay owned by explicit loads and actions
      }
    };
    const t = setInterval(tick, 8000);
    return () => clearInterval(t);
  }, [statusFilter]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await apiService.getExpertActions({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setActions((prev) => [...prev, ...(d?.actions || [])]);
      setNextCursor(d?.nextCursor ?? null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more actions');
    } finally {
      setLoadingMore(false);
    }
  };

  const replaceAction = (updated: ExpertAction | undefined, fallbackId: string) => {
    if (!updated) return;
    setActions((list) => list.map((x) => (x.id === (updated.id || fallbackId) ? updated : x)));
  };

  const doApprove = async (a: ExpertAction) => {
    setActingId(a.id);
    setError(null);
    try {
      const res = await apiService.approveExpertAction(a.id);
      replaceAction(res?.action, a.id);
    } catch (e: any) {
      setError(e?.message || 'Approve failed');
    } finally {
      setActingId(null);
      setConfirmApprove(null);
    }
  };

  const handleDeclined = (updated: ExpertAction) => {
    replaceAction(updated, declining?.id || '');
    setDeclining(null);
  };

  const handleRequested = (updated: ExpertAction) => {
    replaceAction(updated, requesting?.id || '');
    setRequesting(null);
  };

  if (notDeployed) {
    return (
      <div className="xp-panel xp-notdeployed">
        <div className="xp-notdeployed-icon">🚧</div>
        <div className="xp-notdeployed-title">Action queue backend not deployed yet (W2)</div>
        <div className="xp-notdeployed-sub">
          GET /admin/expert-chat/actions returned 404. The approval-queue contract is final and
          this UI is ready — it lights up as soon as the W2 backend PR is live.
        </div>
      </div>
    );
  }

  return (
    <div className="xp-panel">
      <div className="xp-toolbar">
        <div className="xp-filter">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {ACTION_STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'all' : s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <span className="xp-count">
          {loading ? 'loading…' : `${actions.length} action${actions.length === 1 ? '' : 's'} loaded`}
        </span>
        <button className="xp-refresh" onClick={load} disabled={loading}>
          ⟳ Refresh
        </button>
      </div>

      {error && <div className="xp-error">{error}</div>}

      <div className="xp-actions-list">
        {!loading && actions.length === 0 && (
          <div className="xp-empty">No actions with status “{statusFilter}”.</div>
        )}
        {actions.map((a) => {
          const summary = payloadSummary(a);
          return (
            <div className="xp-action-card" key={a.id}>
              <div className="xp-action-head">
                <TypeBadge type={a.type} />
                <ActionStatusBadge status={a.status} />
                <span className="xp-dim xp-action-date">{fmtDate(a.createdAt)}</span>
              </div>

              <div className="xp-action-meta">
                <button
                  className={`xp-user-toggle ${contextFor === a.id ? 'xp-user-toggle-open' : ''}`}
                  title={a.userId}
                  onClick={() => setContextFor(contextFor === a.id ? null : a.id)}
                >
                  👤 <b>{a.user?.username || shortId(a.userId)}</b>
                  {a.user?.email ? <span className="xp-dim"> · {a.user.email}</span> : null}
                  <span className="xp-user-caret">{contextFor === a.id ? '▾' : '▸'}</span>
                </button>
                <span>·</span>
                <span>
                  session{' '}
                  <button
                    className="xp-link xp-mono"
                    title={`open shadow run ${a.sessionId}`}
                    onClick={() => onOpenRun(a.sessionId)}
                  >
                    {shortId(a.sessionId)}
                  </button>
                </span>
              </div>

              {contextFor === a.id && <UserContextSection userId={a.userId} />}

              {a.recommendation && (
                <div className="xp-action-rec">
                  <span className="xp-rec-label">Expert summary</span>
                  {a.recommendation}
                </div>
              )}
              {summary && <div className="xp-action-payload">{summary}</div>}

              <ReviewBox
                review={a.review}
                reviewError={a.reviewError}
                reviewPending={a.status === 'pending_review'}
              />

              {a.status === 'info_requested' && a.infoRequest && (
                <div className="xp-info-req">
                  <span className="xp-info-req-label">⏳ Waiting on the user</span>
                  <div className="xp-info-req-text">“{a.infoRequest.text}”</div>
                </div>
              )}

              <div className="xp-action-json">
                {a.payload != null && (
                  <details className="xp-block xp-block-raw">
                    <summary>payload</summary>
                    <pre>{pretty(a.payload)}</pre>
                  </details>
                )}
                {a.evidence != null && (
                  <details className="xp-block xp-block-raw">
                    <summary>evidence</summary>
                    <pre>{pretty(a.evidence)}</pre>
                  </details>
                )}
              </div>

              {(a.decidedBy || a.executedAt || a.error) && (
                <div className="xp-action-audit">
                  {a.decidedBy && (
                    <span>
                      decided by <b>{a.decidedBy}</b>
                      {a.decidedAt ? ` · ${fmtDate(a.decidedAt)}` : ''}
                    </span>
                  )}
                  {a.executedAt && <span>executed {fmtDate(a.executedAt)}</span>}
                  {a.error && <span className="xp-action-error">⚠ {a.error}</span>}
                </div>
              )}

              {a.status === 'pending_review' && (
                <div className="xp-action-buttons">
                  <button
                    className="xp-approve"
                    disabled={actingId === a.id}
                    onClick={() => setConfirmApprove(a)}
                  >
                    {actingId === a.id
                      ? 'Executing…'
                      : a.type === 'manual_review'
                        ? '✓ Mark handled'
                        : '✓ Approve'}
                  </button>
                  <button
                    className="xp-decline"
                    disabled={actingId === a.id}
                    onClick={() => setDeclining(a)}
                  >
                    ✕ Decline
                  </button>
                  <button
                    className="xp-requestinfo"
                    disabled={actingId === a.id}
                    onClick={() => setRequesting(a)}
                  >
                    ？ Request info
                  </button>
                </div>
              )}

              <AskAIBox actionId={a.id} />
            </div>
          );
        })}
      </div>

      {nextCursor && (
        <div className="xp-loadmore">
          <button onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {confirmApprove && (
        <ConfirmModal
          isOpen
          title={confirmApprove.type === 'manual_review' ? 'Mark manual task handled' : 'Approve & execute action'}
          message={
            confirmApprove.type === 'manual_review'
              ? `Mark this escalated case for ${confirmApprove.user?.username || confirmApprove.userId} as handled? Nothing is executed — this records that a human actioned it.`
              : `Approve "${String(confirmApprove.type).replace(/_/g, ' ')}" for user ${confirmApprove.user?.username || confirmApprove.userId}? The backend will execute this action immediately.`
          }
          confirmText={
            actingId === confirmApprove.id
              ? 'Executing…'
              : confirmApprove.type === 'manual_review'
                ? 'Mark handled'
                : 'Approve & Execute'
          }
          cancelText="Cancel"
          confirmButtonClass="primary"
          onConfirm={() => doApprove(confirmApprove)}
          onCancel={() => setConfirmApprove(null)}
        />
      )}

      {declining && (
        <DeclineModal
          action={declining}
          onClose={() => setDeclining(null)}
          onDeclined={handleDeclined}
        />
      )}

      {requesting && (
        <RequestInfoModal
          action={requesting}
          onClose={() => setRequesting(null)}
          onRequested={handleRequested}
        />
      )}
    </div>
  );
};

/** Independent reviewer verdict — decision support for the human, not a gate. */
// ── User context drawer (founder 2026-07-04) ─────────────────────────────────
// "man sieht nicht den user … user data, KYC images, balance, badges und alle
// historien": every queue card expands into the full support picture.

/** KYC document image behind admin auth (same pattern as PendingKYCPage —
 *  a plain <img src> would 401, so bytes come via the API client → object URL). */
const AuthedKycThumb: React.FC<{ path: string; label: string }> = ({ path, label }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    apiService
      .fetchKYCImageBlob(path)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);
  if (failed) return null;
  return (
    <div className="xp-kyc-thumb">
      <label>{label}</label>
      {objectUrl ? (
        <img src={objectUrl} alt={label} onClick={() => window.open(objectUrl, '_blank')} />
      ) : (
        <div className="xp-kyc-thumb-loading">…</div>
      )}
    </div>
  );
};

const ctxMoney = (v: unknown, ccy: unknown): string =>
  v == null ? '—' : `${String(v)}${ccy ? ` ${String(ccy)}` : ''}`;

const UserContextSection: React.FC<{ userId: string }> = ({ userId }) => {
  const [ctx, setCtx] = useState<ExpertUserContext | null>(null);
  const [kycImages, setKycImages] = useState<Array<{ label: string; url: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCtx(null);
    setError(null);
    setKycImages([]);
    apiService
      .getExpertUserContext(userId)
      .then((d) => {
        if (!cancelled) setCtx(d);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load user context');
      });
    // KYC document IMAGES live on the dedicated, access-logged endpoint
    // (master role) — loaded best-effort, the drawer works without them.
    apiService
      .getKYCDetails(userId)
      .then((d) => {
        if (cancelled || !d?.images) return;
        setKycImages(
          [
            { label: 'Selfie', url: d.images.faceImageUrl },
            { label: 'ID front', url: d.images.idFrontImageUrl },
            { label: 'ID back', url: d.images.idBackImageUrl },
          ].filter((x): x is { label: string; url: string } => Boolean(x.url))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (error) return <div className="xp-ctx xp-error">{error}</div>;
  if (!ctx) return <div className="xp-ctx xp-ctx-loading">Loading user context…</div>;

  const wallet = ctx.wallet || {};
  const profile = ctx.profile || {};
  const kyc = ctx.kyc || {};
  const predictions: any[] = Array.isArray(ctx.predictions?.predictions)
    ? ctx.predictions.predictions
    : [];
  const payments: any[] = Array.isArray(ctx.payments?.payments) ? ctx.payments.payments : [];
  const ledger: any[] = Array.isArray(ctx.ledger?.entries) ? ctx.ledger.entries : [];
  const badges: any[] = Array.isArray(ctx.badges) ? ctx.badges : [];
  const tickets: any[] = Array.isArray(ctx.tickets) ? ctx.tickets : [];
  const recentActions: any[] = Array.isArray(ctx.recentActions) ? ctx.recentActions : [];

  return (
    <div className="xp-ctx">
      <div className="xp-ctx-grid">
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">Account</div>
          <div className="xp-ctx-rows">
            <span>
              <b>{ctx.user.username || '—'}</b>
            </span>
            <span className="xp-dim">{ctx.user.email || '—'}</span>
            <span>member since {fmtDate(ctx.user.created_at)}</span>
            <span>currency {profile.currency || wallet.currency || '—'}</span>
            {ctx.user.frozen_at && (
              <span className="xp-ctx-alert">
                ⛔ frozen · {ctx.user.freeze_reason || 'no reason recorded'}
              </span>
            )}
          </div>
        </div>
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">Balance</div>
          <div className="xp-ctx-rows">
            <span>
              available <b>{ctxMoney(wallet.balance, wallet.currency)}</b>
            </span>
            <span>locked {ctxMoney(wallet.locked_balance, wallet.currency)}</span>
            <span>bonus {ctxMoney(wallet.bonus_balance, wallet.currency)}</span>
          </div>
        </div>
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">KYC</div>
          <div className="xp-ctx-rows">
            <span>
              status <b>{kyc.kyc_status || ctx.user.kyc_status || '—'}</b>
            </span>
            {kyc.submitted_at && <span>submitted {fmtDate(kyc.submitted_at)}</span>}
            {kyc.rejection_reason && (
              <span className="xp-ctx-alert">rejected · {kyc.rejection_reason}</span>
            )}
          </div>
          {kycImages.length > 0 && (
            <div className="xp-kyc-thumbs">
              {kycImages.map((img) => (
                <AuthedKycThumb key={img.label} path={img.url} label={img.label} />
              ))}
            </div>
          )}
        </div>
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">Badges ({badges.length})</div>
          <div className="xp-ctx-chips">
            {badges.length === 0 && <span className="xp-dim">none</span>}
            {badges.map((b: any) => (
              <span className="xp-ctx-chip" key={b.id} title={b.badgeDescription || ''}>
                {b.badgeIcon ? `${b.badgeIcon} ` : ''}
                {b.badgeName || b.badgeId}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="xp-ctx-lists">
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">Recent predictions ({predictions.length})</div>
          <table className="xp-ctx-table">
            <tbody>
              {predictions.length === 0 && (
                <tr>
                  <td className="xp-dim">none</td>
                </tr>
              )}
              {predictions.map((b: any) => (
                <tr key={b.bet_id}>
                  <td className="xp-ctx-ellip" title={b.market_headline}>
                    {b.market_headline || b.market_id}
                  </td>
                  <td>{b.outcome}</td>
                  <td>{ctxMoney(b.stake, b.currency)}</td>
                  <td>@{b.odds}</td>
                  <td>
                    <span className={`xp-badge xp-act-${b.status}`}>{b.status}</span>
                  </td>
                  <td className="xp-dim">{fmtDate(b.placed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">
            Payments ({payments.length}) · Ledger ({ledger.length})
          </div>
          <table className="xp-ctx-table">
            <tbody>
              {payments.map((p: any) => (
                <tr key={p.payment_id}>
                  <td>{p.method || 'payment'}</td>
                  <td>{ctxMoney(p.amount, p.currency)}</td>
                  <td>
                    <span className={`xp-badge xp-act-${p.status}`}>{p.status}</span>
                  </td>
                  <td className="xp-dim">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
              {ledger.slice(0, 8).map((e: any, i: number) => (
                <tr key={e.id || `l${i}`}>
                  <td>{String(e.type || 'ledger').replace(/_/g, ' ')}</td>
                  <td>{ctxMoney(e.amount, e.currency)}</td>
                  <td className="xp-ctx-ellip" title={e.description}>
                    {e.description || ''}
                  </td>
                  <td className="xp-dim">{fmtDate(e.created_at)}</td>
                </tr>
              ))}
              {payments.length === 0 && ledger.length === 0 && (
                <tr>
                  <td className="xp-dim">none</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="xp-ctx-block">
          <div className="xp-ctx-title">
            Tickets ({tickets.length}) · Expert actions ({recentActions.length})
          </div>
          <table className="xp-ctx-table">
            <tbody>
              {tickets.map((t: any) => (
                <tr key={t.id}>
                  <td className="xp-ctx-ellip" title={t.subject}>
                    {t.subject || t.id}
                  </td>
                  <td>
                    <span className={`xp-badge xp-act-${t.status}`}>
                      {String(t.status).replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="xp-dim">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
              {recentActions.map((ra: any) => (
                <tr key={ra.id}>
                  <td>
                    <TypeBadge type={ra.type} />
                  </td>
                  <td>
                    <ActionStatusBadge status={ra.status} />
                  </td>
                  <td className="xp-dim">{fmtDate(ra.created_at)}</td>
                </tr>
              ))}
              {tickets.length === 0 && recentActions.length === 0 && (
                <tr>
                  <td className="xp-dim">none</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── "Ask the AI about this case" (read-only Q&A, versusapp PR #528) ──────────
// A per-card scratch thread; the AI answers about THIS action with context and
// never mutates. Thread is in-memory only (agent-facing scratch — no need to
// persist across reloads). Collapsed behind a toggle, mirroring the 👤 drawer.
const AskAIBox: React.FC<{ actionId: string }> = ({ actionId }) => {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<ExpertChatTurn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    // Optimistically show the agent's question; send prior turns as history.
    const history = thread;
    setThread((t) => [...t, { role: 'agent', content: q }]);
    setQuestion('');
    try {
      const res = await apiService.askExpertAboutAction(actionId, q, history);
      setThread((t) => [...t, { role: 'assistant', content: res?.answer ?? '(no answer)' }]);
    } catch (err: any) {
      setError(
        err?.status === 404
          ? 'Ask endpoint not deployed on this environment yet.'
          : err?.message || 'The AI could not answer — try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="xp-ask">
      <button
        type="button"
        className={`xp-ask-toggle ${open ? 'xp-ask-toggle-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        🤖 Ask the AI about this case
        <span className="xp-user-caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="xp-ask-body">
          {thread.length > 0 && (
            <div className="xp-ask-thread">
              {thread.map((m, i) => (
                <div key={i} className={`xp-ask-msg xp-ask-${m.role}`}>
                  <span className="xp-ask-role">{m.role === 'agent' ? 'You' : 'AI'}</span>
                  <span className="xp-ask-text">{m.content}</span>
                </div>
              ))}
              {busy && (
                <div className="xp-ask-msg xp-ask-assistant xp-ask-thinking">
                  <span className="xp-ask-role">AI</span>
                  <span className="xp-ask-text xp-dim">
                    <span className="xp-ask-dots" /> thinking…
                  </span>
                </div>
              )}
            </div>
          )}
          {error && <div className="xp-error-inline xp-ask-error">{error}</div>}
          <form className="xp-ask-form" onSubmit={submit}>
            <input
              className="xp-form-input xp-ask-input"
              placeholder={
                thread.length ? 'Ask a follow-up…' : 'e.g. is this refund amount justified?'
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={busy}
              maxLength={2000}
            />
            <button
              type="submit"
              className="xp-ask-send"
              disabled={busy || !question.trim()}
            >
              {busy ? '…' : 'Ask'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const ReviewBox: React.FC<{
  review?: ExpertActionReview | null;
  reviewError?: string | null;
  /** pending_review actions appear in the queue BEFORE the async reviewer returns. */
  reviewPending?: boolean;
}> = ({ review, reviewError, reviewPending }) => {
  if (reviewError) {
    return (
      <div className="xp-review xp-review-failed">
        <span className="xp-review-badge xp-rev-failed">reviewer unavailable</span>
        <span className="xp-review-reasoning">Decide without a recommendation. ({reviewError})</span>
      </div>
    );
  }
  if (!review) {
    // The proposal reaches this queue instantly; the independent review runs
    // in the background and its recommendation attaches on the next refresh.
    if (reviewPending) {
      return (
        <div className="xp-review xp-review-running">
          <span className="xp-review-badge xp-rev-running">⏳ compliance review running…</span>
          <span className="xp-review-reasoning xp-dim">
            The independent reviewer&apos;s recommendation will attach here shortly. You can
            already decide without it.
          </span>
        </div>
      );
    }
    return null;
  }
  const cls =
    review.recommendation === 'approve'
      ? 'xp-rev-approve'
      : review.recommendation === 'decline'
        ? 'xp-rev-decline'
        : 'xp-rev-info';
  const label =
    review.recommendation === 'approve'
      ? '✓ recommends approve'
      : review.recommendation === 'decline'
        ? '✕ recommends decline'
        : '？ needs info';
  return (
    <div className="xp-review">
      {review.summary && <div className="xp-review-summary">{review.summary}</div>}
      <div className="xp-review-head">
        <RatingChip rating={review.rating} />
        <span className={`xp-review-badge ${cls}`}>{label}</span>
        {review.model && <span className="xp-review-model xp-dim">{review.model}</span>}
      </div>
      {review.reasoning && <div className="xp-review-reasoning">{review.reasoning}</div>}
      {review.recommendation === 'need_info' && review.infoNeeded && (
        <div className="xp-review-needed">needs: {review.infoNeeded}</div>
      )}
      {review.riskFlags && review.riskFlags.length > 0 && (
        <div className="xp-review-flags">
          {review.riskFlags.map((f, i) => (
            <span className="xp-risk-flag" key={i}>
              ⚠ {f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestInfoModal: React.FC<{
  action: ExpertAction;
  onClose: () => void;
  onRequested: (a: ExpertAction) => void;
}> = ({ action, onClose, onRequested }) => {
  // Pre-fill with the reviewer's stated info need, if any — the common case.
  const [instruction, setInstruction] = useState(
    action.review?.recommendation === 'need_info' ? action.review?.infoNeeded || '' : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!instruction.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.requestExpertActionInfo(action.id, instruction.trim());
      onRequested(res.action);
    } catch (err: any) {
      setError(err?.message || 'Request failed');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="xp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Ask the user for more information</h3>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="xp-form-group">
              <label className="xp-form-label">What should the expert ask the user?</label>
              <textarea
                className="xp-form-textarea"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder="e.g. the transaction ID of the disputed deposit"
                rows={3}
                autoFocus
                maxLength={2000}
              />
              <div className="xp-form-hint">
                The user gets this question in their chat; their reply re-enters the expert loop
                (a fresh proposal &amp; review). The action parks as “info requested”.
              </div>
            </div>
            {error && <div className="xp-error-inline">{error}</div>}
          </div>
          <div className="modal-actions xp-modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="button-primary" disabled={busy || !instruction.trim()}>
              {busy ? 'Sending…' : 'Send question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeclineModal: React.FC<{
  action: ExpertAction;
  onClose: () => void;
  onDeclined: (a: ExpertAction) => void;
}> = ({ action, onClose, onDeclined }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.declineExpertAction(action.id, reason.trim());
      onDeclined(res?.action || { ...action, status: 'declined' });
    } catch (err: any) {
      setError(err?.message || 'Decline failed');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content xp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Decline action</h2>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            {error && <div className="xp-error">{error}</div>}
            <p className="xp-decline-what">
              Declining <b>{String(action.type).replace(/_/g, ' ')}</b> for user{' '}
              <span className="xp-mono">{shortId(action.userId)}</span>.
            </p>
            <div className="xp-form-group">
              <label className="xp-form-label">Reason * (audit log)</label>
              <textarea
                className="xp-form-input xp-form-textarea"
                placeholder="Why is this action being declined?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                required
              />
            </div>
          </div>
          <div className="modal-actions xp-modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="button-danger" disabled={busy || !reason.trim()}>
              {busy ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VersusExpertPage;
